import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, Download, BookHeart } from "lucide-react";
import { toast } from "sonner";
import { http, API } from "@/lib/api";

const SESSION_KEY = "sf_session";
const EMAIL_KEY = "sf_email";

function SignInCard({ email, setEmail, sending, onSend }) {
  return (
    <div className="paper-card mt-8 p-8 max-w-md" data-testid="magic-form">
      <div className="text-center mb-4">
        <BookHeart className="mx-auto text-[#E07A5F] mb-2" size={36} />
        <div className="font-display text-2xl font-bold">Sign in with email</div>
        <div className="text-sm text-[#5C6370]">We'll send a one-tap magic link.</div>
      </div>
      <Label>Email</Label>
      <div className="relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" size={16} />
        <Input data-testid="library-email" className="pl-9" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} placeholder="parent@example.com" />
      </div>
      <Button data-testid="send-link-btn" disabled={sending || !email} onClick={onSend}
        type="submit"
        className="mt-4 w-full bg-[#E07A5F] hover:bg-[#D96B43] text-white rounded-full py-5">
        {sending ? <><Loader2 className="animate-spin mr-2" size={16} /> Sending…</> : "Email me my link"}
      </Button>
    </div>
  );
}

function BookCard({ book }) {
  return (
    <div className="paper-card overflow-hidden" data-testid={`book-${book.order_id}`}>
      <div className="h-40 bg-gradient-to-br from-[#F2CC8F] via-[#E07A5F] to-[#6B4E71] flex items-center justify-center text-white">
        <BookHeart size={40} />
      </div>
      <div className="p-5">
        <div className="font-display text-lg font-bold text-[#2B2D42]">{book.title || "Untitled story"}</div>
        <div className="text-xs text-[#5C6370] mt-1">For {book.child_name}</div>
        <a href={`${API}/download/${book.order_id}`} target="_blank" rel="noreferrer">
          <Button className="mt-4 w-full bg-[#2B2D42] hover:bg-[#3D405B] text-white rounded-full">
            <Download size={14} className="mr-2" /> Download
          </Button>
        </a>
      </div>
    </div>
  );
}

function BookGrid({ loading, books }) {
  if (loading) {
    return <div className="text-[#5C6370]"><Loader2 className="animate-spin inline mr-2" /> Loading…</div>;
  }
  if (books.length === 0) {
    return (
      <div className="paper-card p-10 text-center">
        <BookHeart className="mx-auto text-[#E07A5F] mb-3" size={36} />
        <div className="font-display text-2xl font-bold">No books yet</div>
        <p className="text-[#5C6370] mt-2">Make your first one — it takes 90 seconds.</p>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="books-grid">
      {books.map((b) => <BookCard key={b.order_id} book={b} />)}
    </div>
  );
}

export default function Library() {
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(() => sessionStorage.getItem(EMAIL_KEY) || "");
  const [token, setToken] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const signOut = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
    setToken("");
    setBooks([]);
  }, []);

  // Verify magic-link params on load
  useEffect(() => {
    const t = sp.get("token");
    const e = sp.get("email");
    if (!t || !e) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.post("/auth/verify", { token: t, email: e });
        if (cancelled) return;
        sessionStorage.setItem(SESSION_KEY, data.session_token);
        sessionStorage.setItem(EMAIL_KEY, data.email);
        setToken(data.session_token);
        setEmail(data.email);
        toast.success("Signed in!");
        window.history.replaceState({}, "", "/library");
      } catch (err) {
        console.warn("Magic-link verify failed:", err?.response?.data || err?.message);
        toast.error("Invalid or expired link");
      }
    })();
    return () => { cancelled = true; };
  }, [sp]);

  // Fetch library whenever we have a session
  useEffect(() => {
    if (!email || !token) return;
    let cancelled = false;
    setLoading(true);
    http.get(`/library?email=${encodeURIComponent(email)}&token=${token}`)
      .then((r) => { if (!cancelled) setBooks(r.data.books || []); })
      .catch((err) => {
        console.warn("Library fetch failed:", err?.response?.data || err?.message);
        if (!cancelled) signOut();
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [email, token, signOut]);

  const sendLink = async () => {
    if (!email) { toast.error("Enter your email"); return; }
    setSending(true);
    try {
      const { data } = await http.post("/auth/magic-link", { email, origin_url: window.location.origin });
      toast.success("Check your inbox for the magic link ✦");
      if (data.dev_link) console.log("Dev magic link:", data.dev_link);
    } catch (err) {
      console.warn("Magic link send failed:", err?.response?.data || err?.message);
      toast.error("Couldn't send link");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-4xl mx-auto px-5 py-10">
        <h1 className="font-display text-4xl font-bold text-[#2B2D42]">My library</h1>
        <p className="text-[#4A4E69] mt-2">Every book you've made — always yours to re-download.</p>

        {!token ? (
          <SignInCard email={email} setEmail={setEmail} sending={sending} onSend={sendLink} />
        ) : (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-[#5C6370]">Signed in as <strong>{email}</strong></div>
              <button onClick={signOut} className="text-xs text-[#E07A5F] hover:underline">Sign out</button>
            </div>
            <BookGrid loading={loading} books={books} />
          </div>
        )}
      </main>
    </div>
  );
}
